/**
 * Boli Live - Store Header Component
 *
 * eBay-style store banner with info and tab navigation.
 */

import { Star, ShieldCheck, UserPlus } from 'lucide-react';
import type { Store } from '@boli/types';
import { cn } from '../lib/utils';
import { formatCompact } from '../lib/utils';

interface StoreHeaderProps {
  store: Store;
  activeTab: 'products' | 'auctions' | 'about';
  onTabChange: (tab: 'products' | 'auctions' | 'about') => void;
  className?: string;
}

export function StoreHeader({ store, activeTab, onTabChange, className }: StoreHeaderProps) {
  const tabs = [
    { id: 'products' as const, label: 'Products' },
    { id: 'auctions' as const, label: 'Auctions' },
    { id: 'about' as const, label: 'About' },
  ];

  return (
    <div className={cn('bg-card border-b border-border', className)}>
      {/* Banner Image */}
      <div className="relative h-32 sm:h-40 md:h-48 bg-gradient-to-r from-primary/20 to-primary/10 overflow-hidden">
        {store.banner ? (
          <img
            src={store.banner}
            alt={`${store.name} banner`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 via-primary/20 to-secondary" />
        )}
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Store Info */}
      <div className="relative px-4 pb-4">
        {/* Logo - overlapping banner */}
        <div className="absolute -top-10 left-4 sm:-top-12 sm:left-6">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-card bg-card shadow-lg overflow-hidden">
            {store.logo ? (
              <img
                src={store.logo}
                alt={store.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-secondary flex items-center justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-secondary-foreground uppercase">
                  {store.name.charAt(0)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Store Details */}
        <div className="pt-12 sm:pt-14 sm:pl-28 md:pl-32">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1">
              {/* Name and Verified Badge */}
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                  {store.name}
                </h1>
                {store.isVerified && (
                  <ShieldCheck className="w-5 h-5 text-blue-500" />
                )}
                {store.isTopRated && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded uppercase">
                    Top Rated
                  </span>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_: unknown, i: number) => (
                    <Star
                      key={i}
                      className={cn(
                        'w-4 h-4',
                        i < Math.floor(store.rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground/30'
                      )}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-foreground">
                  {store.rating.toFixed(1)}
                </span>
                <span className="text-sm text-muted-foreground">
                  ({formatCompact(store.totalReviews)} reviews)
                </span>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-3 mt-2 text-sm">
                <span className="text-green-600 font-medium">
                  {store.positivePercentage}% Positive
                </span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">
                  {formatCompact(store.totalSales)} items sold
                </span>
              </div>
            </div>

            {/* Follow Button */}
            <button
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-medium"
              onClick={() => {
                // Follow store functionality - future feature
              }}
            >
              <UserPlus className="w-4 h-4" />
              Follow Store
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 border-t border-border pt-2 -mx-4 px-4 overflow-x-auto">
          <nav className="flex gap-1 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-t-md transition-colors relative',
                  activeTab === tab.id
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

// Skeleton for StoreHeader
export function StoreHeaderSkeleton() {
  return (
    <div className="bg-card border-b border-border">
      {/* Banner Skeleton */}
      <div className="h-32 sm:h-40 md:h-48 bg-muted animate-pulse" />

      {/* Store Info Skeleton */}
      <div className="relative px-4 pb-4">
        {/* Logo Skeleton */}
        <div className="absolute -top-10 left-4 sm:-top-12 sm:left-6">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-card bg-muted animate-pulse" />
        </div>

        {/* Details Skeleton */}
        <div className="pt-12 sm:pt-14 sm:pl-28 md:pl-32">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 space-y-2">
              <div className="h-7 w-48 bg-muted rounded animate-pulse" />
              <div className="h-4 w-32 bg-muted rounded animate-pulse" />
              <div className="h-4 w-40 bg-muted rounded animate-pulse" />
            </div>
            <div className="h-10 w-32 bg-muted rounded-md animate-pulse" />
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="mt-4 border-t border-border pt-2">
          <div className="flex gap-4">
            <div className="h-8 w-20 bg-muted rounded animate-pulse" />
            <div className="h-8 w-20 bg-muted rounded animate-pulse" />
            <div className="h-8 w-16 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}
